// Mission API Types
export interface MissionScoring {
  mds: number;
  oss: number;
  completionRate: number;
  mrs: number;
}

export interface MissionParticipationStatus {
  status: 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  progress: number;
  started_at: string | null;
  completed_at: string | null;
  quality_score: number;
  points_earned: number;
}

export interface Mission {
  mission_id: string;
  title: string;
  description: string;
  difficulty: number;
  points_reward: number;
  estimated_time: string;
  priority: 'LOW' | 'MEDIUM' | 'HIGH';
  category: string;
  scoring: MissionScoring;
  participation_status: MissionParticipationStatus;
  call_to_action: string;
  benefits: string[];
  urgency: 'LOW' | 'MEDIUM' | 'HIGH';
}

export interface MissionSummary {
  total_completed: number;
  total_points: number;
  average_quality: number;
  available_to_start: number;
  currently_in_progress: number;
  completion_rate: number;
}

export interface RecommendedMissions {
  not_started: Mission[];
  in_progress: Mission[];
  summary: MissionSummary;
}

export interface MissionOutlet {
  outlet_id: string;
  name: string;
  outlet_type: string;
  region: string;
}

export interface MissionsResponse {
  success: boolean;
  data: {
    outlet: MissionOutlet;
    recommended_missions: RecommendedMissions;
    ai_message: string;
  };
}

// Helper functions
export const getMissionStatusText = (status: string): string => {
  const statusMap: Record<string, string> = {
    'NOT_STARTED': 'Chưa bắt đầu',
    'IN_PROGRESS': 'Đang thực hiện',
    'COMPLETED': 'Hoàn thành',
    'CANCELLED': 'Đã hủy'
  };
  return statusMap[status] || status;
};

export const getMissionStatusColor = (status: string): string => {
  const colorMap: Record<string, string> = {
    'NOT_STARTED': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
    'IN_PROGRESS': 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 hover:bg-blue-100 dark:hover:bg-blue-900',
    'COMPLETED': 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900',
    'CANCELLED': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900'
  };
  return colorMap[status] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800';
};

export const getPriorityColor = (priority: string): string => {
  const colorMap: Record<string, string> = {
    'LOW': 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800',
    'MEDIUM': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900',
    'HIGH': 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900'
  };
  return colorMap[priority] || 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800';
};

export const getPriorityText = (priority: string): string => {
  const textMap: Record<string, string> = {
    'LOW': 'Thấp',
    'MEDIUM': 'Trung bình',
    'HIGH': 'Cao'
  };
  return textMap[priority] || priority;
};

export const getDifficultyText = (difficulty: number): string => {
  if (difficulty <= 1) return 'Dễ';
  if (difficulty <= 2) return 'Trung bình';
  if (difficulty <= 3) return 'Khó';
  return 'Rất khó';
};

export const getDifficultyColor = (difficulty: number): string => {
  if (difficulty <= 1) return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200 hover:bg-green-100 dark:hover:bg-green-900';
  if (difficulty <= 2) return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900';
  if (difficulty <= 3) return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200 hover:bg-orange-100 dark:hover:bg-orange-900';
  return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200 hover:bg-red-100 dark:hover:bg-red-900';
};

export const formatMissionPoints = (points: number): string => {
  return `${points.toLocaleString('vi-VN')} điểm`;
};

export const isMissionAvailable = (status: string): boolean => {
  return status === 'NOT_STARTED';
};

export const isMissionInProgress = (status: string): boolean => {
  return status === 'IN_PROGRESS';
};

export const isMissionCompleted = (status: string): boolean => {
  return status === 'COMPLETED';
};

export const getDifficultyScoreText = (mds: number): string => {
  if (mds >= 150) return `Độ khó cao`;
  if (mds >= 120) return `Độ khó trung bình`;
  if (mds >= 90) return `Độ khó thấp`;
  return `Độ khó rất thấp`;
};

export const getSuitabilityScoreText = (oss: number): string => {
  if (oss >= 90) return `Rất phù hợp`;
  if (oss >= 70) return `Phù hợp`;
  if (oss >= 50) return `Khá phù hợp`;
  return `Ít phù hợp`;
};

export const getDifficultyScoreColor = (mds: number): string => {
  if (mds >= 150) return 'text-red-600 dark:text-red-400 hover:text-red-600 dark:hover:text-red-400';
  if (mds >= 120) return 'text-orange-600 dark:text-orange-400 hover:text-orange-600 dark:hover:text-orange-400';
  if (mds >= 90) return 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-400';
  return 'text-green-600 dark:text-green-400 hover:text-green-600 dark:hover:text-green-400';
};

export const getSuitabilityScoreColor = (oss: number): string => {
  if (oss >= 90) return 'text-green-600 dark:text-green-400 hover:text-green-600 dark:hover:text-green-400';
  if (oss >= 70) return 'text-blue-600 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-400';
  if (oss >= 50) return 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-400';
  return 'text-red-600 dark:text-red-400 hover:text-red-600 dark:hover:text-red-400';
};

export const formatEstimatedTime = (time: string): string => {
  return time
    .replace('minutes', 'phút')
    .replace('minute', 'phút')
    .replace('hours', 'giờ')
    .replace('hour', 'giờ');
};

export const getRecommendationScoreText = (mrs: number): string => {
  if (mrs >= 150) return `Đề xuất cao`;
  if (mrs >= 120) return `Đề xuất trung bình`;
  if (mrs >= 90) return `Đề xuất thấp`;
  return `Ít được đề xuất`;
};

export const getRecommendationScoreColor = (mrs: number): string => {
  if (mrs >= 150) return 'text-green-600 dark:text-green-400 hover:text-green-600 dark:hover:text-green-400';
  if (mrs >= 120) return 'text-blue-600 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-400';
  if (mrs >= 90) return 'text-yellow-600 dark:text-yellow-400 hover:text-yellow-600 dark:hover:text-yellow-400';
  return 'text-gray-600 dark:text-gray-400 hover:text-gray-600 dark:hover:text-gray-400';
};
